import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Icon } from "@iconify/react";
import debounce from "lodash/debounce";
import useApi from "../../hook/useApi";
import { toast } from "react-toastify";
import Loader from "../Loader/Loader";
import { formatCurrency } from "../../utils/helper";
import { PAGE_SIZE_OPTIONS } from "../../constants/pageSizeOptions";
import SearchableSelect from "../Common/SearchableSelect";
import "../Accounts/ledger-page.css";
import "./margin-management.css";

const formatBrokerCell = (row) => {
  const details = row?.brokerDetails;
  if (!Array.isArray(details) || details.length === 0) return "—";
  const labels = details
    .map((d) => `${d?.brokerId?.name} (${d?.brokerId?.loginId})` || d?.brokerId?.loginId)
    .filter(Boolean);
  return labels.length ? labels.join(", ") : "—";
};

const SortHint = () => (
  <Icon
    icon="tabler:selector"
    className="icon ms-1 opacity-50 align-middle"
    style={{ fontSize: "1rem" }}
  />
);

const MarginManagementLayer = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState({
    pageNo: 1,
    limitVal: 10,
    search: "",
    segment: "",
    client: "",
    broker: ""
  });
  const [meta, setMeta] = useState({
    total: 0,
    totalPages: 1,
    currentPage: 1,
  });
  const [clients, setClients] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [segments, setSegments] = useState([]);
  const [uiFilters, setUiFilters] = useState({
    segment: "",
    broker: "",
    brokerId: "",
    userId: "",
  });

  const { AsyncGetApiCall } = useApi();

  const searchDebounceRef = useRef(
    debounce((value) => {
      setQuery((prev) => ({ ...prev, search: value, pageNo: 1 }));
    }, 500),
  );

  useEffect(() => {
    const debounced = searchDebounceRef.current;
    return () => debounced.cancel();
  }, []);

  const fetchMarginList = useCallback(async () => {
    setLoading(true);
    const result = await AsyncGetApiCall("/orders/margin-list", query);

    if (result?.errors) {
      toast.error(result.errors[0]);
      setRows([]);
      setMeta({ total: 0, totalPages: 1, currentPage: 1 });
    } else if (result?.success) {
      setRows(Array.isArray(result.data) ? result.data : []);
      setMeta({
        total: result.total ?? 0,
        totalPages: Math.max(1, result.totalPages ?? 1),
        currentPage: result.currentPage ?? query.pageNo,
      });
    } else {
      setRows([]);
      setMeta({ total: 0, totalPages: 1, currentPage: 1 });
    }
    setLoading(false);
  }, [
    AsyncGetApiCall,
    query.client,
    query.limitVal,
    query.pageNo,
    query.search,
    query.segment,
    query.broker
  ]);

  useEffect(() => {
    fetchMarginList();
  }, [fetchMarginList]);

  useEffect(() => {
    const fetchDropdownData = async () => {
      const [clientRes, brokerRes, segmentRes] = await Promise.all([
        AsyncGetApiCall("/users/my-clients"),
        AsyncGetApiCall("/broker"),
        AsyncGetApiCall("/trading-settings/segments/list?status=active&inSelectMode=true"),
      ]);
      if (clientRes?.success) setClients(clientRes.data || []);
      if (brokerRes?.success) setBrokers(brokerRes.data || []);
      if (segmentRes?.success) {
        const segmentsData = segmentRes?.data.map((s) => s.name);
        setSegments(segmentsData || []);
      }
    };
    fetchDropdownData();
  }, [AsyncGetApiCall]);

  const [searchInput, setSearchInput] = useState("");
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    searchDebounceRef.current(value);
  };

  const safePage = Math.min(query.pageNo, Math.max(1, meta.totalPages));
  const totalPages = meta.totalPages;
  const total = meta.total;
  const pageSize = query.limitVal;
  const showingFrom = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const showingTo = total === 0 ? 0 : Math.min(safePage * pageSize, total);

  useEffect(() => {
    if (query.pageNo > meta.totalPages && meta.totalPages >= 1) {
      setQuery((prev) => ({ ...prev, pageNo: meta.totalPages }));
    }
  }, [meta.totalPages, query.pageNo]);

  const pageButtons = useMemo(() => {
    const n = totalPages;
    if (n <= 0) return [];
    const cap = Math.min(n, 5);
    const out = [];
    for (let i = 0; i < cap; i += 1) {
      let pageNum;
      if (n <= 5) pageNum = i + 1;
      else if (safePage <= 3) pageNum = i + 1;
      else if (safePage >= n - 2) pageNum = n - 4 + i;
      else pageNum = safePage - 2 + i;
      out.push(pageNum);
    }
    return out;
  }, [totalPages, safePage]);

  return (
    <>
      {loading && <Loader />}
      <div className="d-flex align-items-center gap-2 text-secondary-light mb-24">
        <Icon icon="tabler:layout-grid" className="icon text-xl" />
        <Icon icon="tabler:chevron-right" className="icon text-sm" />
        <span className="fw-semibold text-primary-light">
          Margin Management
        </span>
      </div>

      <div className="card border shadow-sm radius-16 mb-24 ledger-filter-card margin-filter-card">
        <div className="card-body p-24">
          <div className="row g-4 align-items-end">
            <div className="col-12 col-md-6 col-lg-4 col-xl-2">
              <label className="form-label fw-bold text-primary-light mb-8 d-block text-uppercase small">
                Segment
              </label>
              <SearchableSelect
                placeholder="All Segments"
                value={uiFilters.segment}
                options={segments.map((segment) => ({
                  label: segment,
                  value: segment,
                }))}
                onChange={(e) =>
                  setUiFilters((prev) => ({ ...prev, segment: e.target.value }))
                }
              />
            </div>
            <div className="col-12 col-md-6 col-lg-4 col-xl-2">
              <label className="form-label fw-bold text-primary-light mb-8 d-block text-uppercase small">
                Broker
              </label>
              <SearchableSelect
                placeholder="All Brokers"
                value={uiFilters.broker}
                options={brokers.map((broker) => ({
                  label: broker.name,
                  value: broker.name,
                  id: broker._id,
                }))}
                onChange={(e) => {
                  console.log("e", e.target);
                  setUiFilters((prev) => ({
                    ...prev,
                    broker: e.target.value,
                    brokerId: e.target.id,
                  }))
                }
                }
              />
            </div>
            <div className="col-12 col-md-6 col-lg-4 col-xl-2">
              <label className="form-label fw-bold text-primary-light mb-8 d-block text-uppercase small">
                Client
              </label>
              <SearchableSelect
                placeholder="All Clients"
                value={uiFilters.userId}
                options={clients.map((client) => ({
                  label: `${client.loginId} — ${client.name}`,
                  value: client.loginId,
                }))}
                onChange={(e) =>
                  setUiFilters((prev) => ({ ...prev, userId: e.target.value }))
                }
              />
            </div>
            <div className="col-12 col-lg-12 col-xl-6 d-flex flex-wrap align-items-center gap-3 mt-xl-0 mt-2">
              <button
                type="button"
                className="btn btn-sm radius-8 px-20 py-10 btn-ledger-apply"
                onClick={() =>
                  setQuery((prev) => ({
                    ...prev,
                    pageNo: 1,
                    segment: uiFilters.segment || "",
                    client: uiFilters.userId || "",
                    broker: uiFilters.brokerId || "",
                  }))
                }
              >
                Apply
              </button>
              <button
                type="button"
                className="btn btn-sm radius-8 px-20 py-10 btn-ledger-clear"
                onClick={() => {
                  setUiFilters({
                    segment: "",
                    broker: "",
                    userId: "",
                  });
                  setQuery((prev) => ({
                    ...prev,
                    pageNo: 1,
                    segment: "",
                    broker: "",
                    client: "",
                  }));
                }}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card border shadow-sm radius-16 ledger-table-card">
        <div className="card-body p-24 pb-0 position-relative" style={{ zIndex: 10 }}>
          <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between gap-3 mb-16">
            <div className="d-flex align-items-center gap-2">
              <span className="text-secondary-light">Show</span>
              <SearchableSelect
                placeholder="Show"
                value={String(query.limitVal)}
                showSearch={false}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  setQuery((prev) => ({
                    ...prev,
                    limitVal: value,
                    pageNo: 1,
                  }));
                }}
                options={PAGE_SIZE_OPTIONS.map((n) => ({
                  label: String(n),
                  value: String(n),
                }))}
              />
            </div>
            <div
              className="position-relative flex-grow-1"
              style={{ maxWidth: "300px" }}
            >
              <Icon
                icon="ion:search-outline"
                className="icon position-absolute top-50 start-0 translate-middle-y ms-12 text-secondary-light"
              />
              <input
                type="search"
                className="form-control ps-40 h-40-px radius-12"
                placeholder="Search by Client / Broker"
                value={searchInput}
                onChange={handleSearchChange}
              />
            </div>
          </div>
        </div>

        <div className="table-responsive px-16 pb-16">
          <table className="table bordered-table sm-table mb-0 text-nowrap">
            <thead>
              <tr className="bg-neutral-100">
                <th className="fw-semibold text-primary-light text-uppercase small">
                  SR <SortHint />
                </th>
                <th className="fw-semibold text-primary-light text-uppercase small">
                  Client <SortHint />
                </th>
                <th className="fw-semibold text-primary-light text-uppercase small">
                  Broker <SortHint />
                </th>
                <th className="fw-semibold text-primary-light text-uppercase small">
                  Segment <SortHint />
                </th>
                <th className="fw-semibold text-primary-light text-uppercase small">
                  Available Margin <SortHint />
                </th>
                <th className="fw-semibold text-primary-light text-uppercase small">
                  Margin Limit <SortHint />
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-5 text-secondary-light"
                  >
                    No matching entries
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => (
                  <tr key={`${row._id}-${row.segmentName}-${index}`}>
                    <td className="text-secondary-light">
                      {(safePage - 1) * pageSize + index + 1}
                    </td>
                    <td>
                      <p className="small text-secondary-light mb-0">
                        {row.loginId ?? "—"}
                      </p>
                      <p className="mb-0 text-primary-light fw-medium">
                        {row.name ?? "—"}
                      </p>
                    </td>
                    <td className="text-secondary-light">
                      {formatBrokerCell(row)}
                    </td>
                    <td className="text-primary-light">
                      {row.segmentName ?? "—"}
                    </td>
                    <td className="text-primary-light">
                      {formatCurrency(row.availableMarginLimit) ?? "—"}
                    </td>
                    <td className="text-primary-light">
                      {formatCurrency(row.marginLimit)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="border-top d-flex flex-column flex-md-row align-items-center justify-content-between gap-3 p-24">
          <p className="mb-0 text-secondary-light text-sm">
            Showing{" "}
            <span className="fw-medium text-primary-light">
              {showingFrom} to {showingTo} of {total} entries
            </span>
          </p>
          {total > 0 && (
            <div className="d-flex align-items-center gap-1">
              <button
                type="button"
                className="btn btn-sm border radius-8 w-32-px h-32-px p-0 d-flex align-items-center justify-content-center"
                onClick={() =>
                  setQuery((prev) => ({
                    ...prev,
                    pageNo: Math.max(1, prev.pageNo - 1),
                  }))
                }
                disabled={safePage <= 1}
              >
                <Icon icon="tabler:chevron-left" className="icon" />
              </button>
              {pageButtons.map((pageNum) => (
                <button
                  key={pageNum}
                  type="button"
                  className={`btn btn-sm border radius-8 w-32-px h-32-px p-0 d-flex align-items-center justify-content-center ${pageNum === safePage
                    ? "ledger-pagination-btn active-page-num"
                    : "bg-base text-secondary-light"
                    }`}
                  onClick={() =>
                    setQuery((prev) => ({ ...prev, pageNo: pageNum }))
                  }
                >
                  {pageNum}
                </button>
              ))}
              {totalPages > 5 && safePage < totalPages - 2 && (
                <>
                  <span className="text-secondary-light px-1">…</span>
                  <button
                    type="button"
                    className="btn btn-sm border radius-8 w-32-px h-32-px p-0 d-flex align-items-center justify-content-center bg-base text-secondary-light"
                    onClick={() =>
                      setQuery((prev) => ({
                        ...prev,
                        pageNo: totalPages,
                      }))
                    }
                  >
                    {totalPages}
                  </button>
                </>
              )}
              <button
                type="button"
                className="btn btn-sm border radius-8 w-32-px h-32-px p-0 d-flex align-items-center justify-content-center"
                onClick={() =>
                  setQuery((prev) => ({
                    ...prev,
                    pageNo: Math.min(totalPages, prev.pageNo + 1),
                  }))
                }
                disabled={safePage >= totalPages}
              >
                <Icon icon="tabler:chevron-right" className="icon" />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MarginManagementLayer;
